import { useState, useEffect } from 'react';

type Image = {
  src: string;
  thumb: string;
  alt: string;
  num: number;
};

// Configuration pour la galerie manifestation (un seul dossier)
const GALLERY_CONFIG = {
  title: 'Galerie Manifestation',
  bgImage: 12,
  altPrefix: 'Manifestation Image'
};

function createImageMap(context: any) {
  const images: Record<string, string> = {};
  
  context.keys().forEach((item: string | number) => {
    try {
      const module = context(item);
      const imageSrc = module.default || module;
      if (imageSrc) {
        images[item] = imageSrc;
      }
    } catch (error) {
      console.error('Error loading:', item, error);
    }
  });
  
  return images;
}

// Fonction pour obtenir les contextes du dossier shooting
const getShootingContexts = () => {
  try {
    return {
      full: require.context('../../img/manif', false, /\.webp$/),
      thumbs: require.context('../../img/manif/thumbs', false, /\.webp$/)
    };
  } catch (error) {
    console.error('Erreur lors du chargement du contexte manifestation:', error);
    throw error;
  }
};

export const useGalleryManifImages = () => {
  const [images, setImages] = useState<Image[]>([]);
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadImages = async () => {
      try {
        setLoading(true);
        setError(null);

        // Configuration fixe pour le shooting
        setConfig(GALLERY_CONFIG);

        // Obtenir les contextes pour le dossier shooting
        const contexts = getShootingContexts();
        
        // Charger les images principales et thumbnails
        const fullImages = createImageMap(contexts.full);
        const thumbImages = createImageMap(contexts.thumbs);

        // Construction du tableau final
        const processedImages = Object.keys(fullImages)
          .filter(key => key.includes('image'))
          .map(key => {
            // Extraire le numéro du fichier
            const match = key.match(/image(\d+)\.webp$/);
            const num = match ? match[1] : '0';
            
            // Construire le nom du thumbnail correspondant
            const thumbKey = `./thumb${num}.webp`;
            
            return {
              src: fullImages[key],
              thumb: thumbImages[thumbKey] || fullImages[key], // fallback sur l'image principale
              alt: `${GALLERY_CONFIG.altPrefix} ${num}`,
              num: parseInt(num) || 0
            };
          })
          .filter(image => image.src) // S'assurer que l'image existe
          .sort((a, b) => a.num - b.num); // Trier par numéro

        if (processedImages.length === 0) {
          throw new Error('Aucune image trouvée dans le dossier manifestation.');
        }

        setImages(processedImages);
      } catch (err) {
        console.error('Erreur lors du chargement de la galerie manifestation:', err);
        setError(err instanceof Error ? err.message : 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, []); // Pas de dépendance car un seul dossier

  return { images, config, loading, error };
};